/**
 * Centralized Listing Types Configuration
 * Single source of truth for listing type labels, colors, and translations
 */

export const LISTING_TYPES = {
  sale: {
    label: { en: 'For Sale', nl: 'Te Koop', es: 'En Venta' },
    shortLabel: { en: 'Sale', nl: 'Koop', es: 'Venta' },
    bg: 'bg-blue-600',
    text: 'text-white',
    color: 'from-green-500 to-emerald-600',
  },
  rent: {
    label: { en: 'For Rent', nl: 'Te Huur', es: 'En Alquiler' },
    shortLabel: { en: 'Rent', nl: 'Huur', es: 'Alquiler' },
    bg: 'bg-purple-600',
    text: 'text-white',
    color: 'from-blue-500 to-cyan-600',
  },
  trade: {
    label: { en: 'Trade', nl: 'Ruilen', es: 'Intercambio' },
    shortLabel: { en: 'Trade', nl: 'Ruil', es: 'Intercambio' },
    bg: 'bg-cyan-600',
    text: 'text-white',
    color: 'from-purple-500 to-violet-600',
  },
  service: {
    label: { en: 'Service', nl: 'Dienst', es: 'Servicio' },
    shortLabel: { en: 'Service', nl: 'Dienst', es: 'Servicio' },
    bg: 'bg-orange-600',
    text: 'text-white',
    color: 'from-orange-500 to-amber-600',
  },
  yard_sale: {
    label: { en: 'Yard Sale', nl: 'Garage Sale', es: 'Venta de Garaje' },
    shortLabel: { en: 'Yard Sale', nl: 'Garage Sale', es: 'Garaje' },
    bg: 'bg-amber-500',
    text: 'text-white',
    color: 'from-pink-500 to-rose-600',
  },
  auction: {
    label: { en: 'Auction', nl: 'Veiling', es: 'Subasta' },
    shortLabel: { en: 'Auction', nl: 'Veiling', es: 'Subasta' },
    bg: 'bg-rose-600',
    text: 'text-white',
    color: 'from-red-500 to-rose-600',
  },
  giveaway: {
    label: { en: 'Giveaway', nl: 'Gratis', es: 'Gratis' },
    shortLabel: { en: 'Free', nl: 'Gratis', es: 'Gratis' },
    bg: 'bg-emerald-600',
    text: 'text-white',
    color: 'from-emerald-500 to-green-600',
  },
  bulk: {
    label: { en: 'Bulk Sale', nl: 'Groothandel', es: 'Venta al Mayor' },
    shortLabel: { en: 'Bulk', nl: 'Bulk', es: 'Mayor' },
    bg: 'bg-indigo-600',
    text: 'text-white',
    color: 'from-indigo-500 to-purple-600',
  },
};

/**
 * Get translated listing type label
 * @param {string} type - Listing type (sale, rent, trade, etc.)
 * @param {string} language - Language code (en, nl, es)
 * @param {boolean} short - Use short label
 * @returns {string} Translated label
 */
export const getListingTypeLabel = (type, language = 'en', short = false) => {
  const config = LISTING_TYPES[type];
  if (!config) return type;
  
  const labels = short ? config.shortLabel : config.label;
  return labels?.[language] || labels?.en || type;
};

/**
 * Get listing type style config
 * @param {string} type - Listing type
 * @returns {object} Style config with bg, text, color
 */
export const getListingTypeStyle = (type) => {
  return LISTING_TYPES[type] || { bg: 'bg-gray-600', text: 'text-white', color: 'from-gray-500 to-gray-600' };
};

/**
 * Get all listing types as array for filters/dropdowns
 * @param {string} language - Language code
 * @returns {Array} Array of { value, label } objects
 */
export const getListingTypeOptions = (language = 'en') => {
  return Object.entries(LISTING_TYPES).map(([value, config]) => ({
    value,
    label: config.label[language] || config.label.en,
  }));
};

export default LISTING_TYPES;
